// Recursive element-tree data for the OchrePoint app architecture viewer.
// Mirrors docs/ElementTrees.md — UI → handler → API route → service → external.
// Each node has a kind (drives icon/colour) and an optional source ref + warn flag.

export type NodeKind =
  | 'page'      // route entry page
  | 'ui'        // UI control / component
  | 'handler'   // front-end handler / loader fn
  | 'state'     // local/derived state
  | 'api'       // front-end api-module call
  | 'route'     // backend express route
  | 'service'   // backend service / business logic
  | 'external'  // external process: CLI, pdftoppm, DB, mailer, Stripe, Twilio
  | 'note';     // annotation

export interface TreeNode {
  label: string;
  kind: NodeKind;
  ref?: string;     // e.g. "quotes.routes.ts:120" or "QuoteWizardPage.tsx:452"
  warn?: boolean;   // audit / drift finding
  children?: TreeNode[];
}

export interface FeatureTree {
  id: string;
  title: string;
  route?: string;
  file?: string;
  root: TreeNode;
}

export interface Domain {
  id: string;
  label: string;
  icon: string;        // material-symbols-outlined name
  features: FeatureTree[];
}

const KIND_LABEL: Record<NodeKind, string> = {
  page: 'Page', ui: 'UI', handler: 'Handler', state: 'State',
  api: 'API call', route: 'Route', service: 'Service', external: 'External', note: 'Note',
};
export const NODE_KINDS = KIND_LABEL;

// ── Domains ─────────────────────────────────────────────────────────────────

export const DOMAINS: Domain[] = [
  {
    id: 'auth', label: 'Authentication', icon: 'lock',
    features: [
      {
        id: 'login', title: 'Login', route: '/login', file: 'pages/auth/LoginPage.tsx',
        root: { label: 'LoginPage', kind: 'page', ref: 'LoginPage.tsx', children: [
          { label: 'email/password form → handleSubmit(e)', kind: 'handler', ref: ':47', children: [
            { label: 'login(email, password) — AuthContext', kind: 'api', ref: ':53', children: [
              { label: 'POST /api/auth/login', kind: 'route', ref: 'auth.routes.ts:22', children: [
                { label: 'verify bcrypt · issue access+refresh JWT', kind: 'service' },
                { label: 'tokens → localStorage → /dashboard', kind: 'state' },
              ] },
            ] },
          ] },
          { label: '"Sign in with Google" → handleGoogleSignIn()', kind: 'handler', ref: ':29', children: [
            { label: 'GET /api/auth/google/url → redirect to consent', kind: 'route', ref: 'auth.routes.ts:36' },
          ] },
          { label: 'register link → /register', kind: 'ui' },
        ] },
      },
      {
        id: 'register', title: 'Register', route: '/register', file: 'pages/auth/RegisterPage.tsx',
        root: { label: 'RegisterPage', kind: 'page', ref: 'RegisterPage.tsx', children: [
          { label: 'form → handleSubmit(e)', kind: 'handler', ref: ':17', children: [
            { label: 'register(payload) — AuthContext', kind: 'api', ref: ':36', children: [
              { label: 'POST /api/auth/register', kind: 'route', ref: 'auth.routes.ts:14', children: [
                { label: 'create user + company/team → tokens → /dashboard', kind: 'service' },
              ] },
            ] },
          ] },
        ] },
      },
      {
        id: 'google-callback', title: 'Google OAuth callback', route: '/auth/google/callback', file: 'pages/auth/GoogleCallbackPage.tsx',
        root: { label: 'GoogleCallbackPage', kind: 'page', ref: 'GoogleCallbackPage.tsx', children: [
          { label: 'on mount: read ?token → store', kind: 'state', children: [
            { label: 'authApi.getCurrentUser()', kind: 'api', ref: ':36', children: [
              { label: 'GET /api/auth/me', kind: 'route', ref: 'auth.routes.ts:72' },
            ] },
            { label: 'success → /dashboard · else → /login', kind: 'state' },
          ] },
          { label: 'server: GET /api/auth/google/callback exchanges code', kind: 'route', ref: 'auth.routes.ts:44' },
        ] },
      },
    ],
  },

  {
    id: 'dashboard', label: 'Dashboard', icon: 'dashboard',
    features: [
      {
        id: 'dashboard', title: 'Dashboard', route: '/dashboard', file: 'pages/dashboard/DashboardPage.tsx',
        root: { label: 'DashboardPage', kind: 'page', ref: 'DashboardPage.tsx', children: [
          { label: 'loadDashboardData() (on mount)', kind: 'handler', ref: ':50', children: [
            { label: 'GET /api/dashboard/stats → StatsCard KPIs', kind: 'route', ref: 'dashboard.routes.ts:7' },
            { label: 'GET /api/dashboard/upcoming-jobs → UpcomingJobs', kind: 'route', ref: 'dashboard.routes.ts:8' },
            { label: 'GET /api/dashboard/alerts → AlertsList', kind: 'route', ref: 'dashboard.routes.ts:9' },
            { label: 'GET /api/dashboard/calendar-events → MiniCalendar', kind: 'route', ref: 'dashboard.routes.ts:10' },
            { label: 'GET /api/dashboard/revenue-trend → RevenueTrendChart', kind: 'route', ref: 'dashboard.routes.ts:11' },
            { label: 'GET /api/dashboard/recent-activity → RecentActivity', kind: 'route', ref: 'dashboard.routes.ts:12' },
          ] },
          { label: 'first-run (empty account) → OnboardingWizard overlay', kind: 'ui' },
        ] },
      },
    ],
  },

  {
    id: 'jobs', label: 'Jobs', icon: 'construction',
    features: [
      {
        id: 'jobs-list', title: 'Jobs list', route: '/jobs', file: 'pages/jobs/JobsListPage.tsx',
        root: { label: 'JobsListPage', kind: 'page', ref: 'JobsListPage.tsx', children: [
          { label: 'loadJobs() (search · status · pagination)', kind: 'handler', ref: ':42', children: [
            { label: 'getJobs(params) → GET /api/jobs', kind: 'route', ref: 'jobs.routes.ts:10' },
          ] },
          { label: 'handleSort(column) — client reorder', kind: 'handler', ref: ':81' },
          { label: 'handleEdit(job) → /jobs/:id/edit', kind: 'handler', ref: ':90' },
          { label: 'handleEditSchedule(job) → JobSchedulingForm', kind: 'handler', ref: ':96' },
          { label: 'handleDuplicate(job) → duplicateJob(id)', kind: 'handler', ref: ':104', children: [
            { label: 'POST /api/jobs/:id/duplicate', kind: 'route', ref: 'jobs.routes.ts:22' },
          ] },
          { label: 'handleDelete(job) → ConfirmDialog → deleteJob(id)', kind: 'handler', ref: ':115', children: [
            { label: 'DELETE /api/jobs/:id', kind: 'route', ref: 'jobs.routes.ts:30' },
          ] },
          { label: 'handleNewJobSubmit → POST /api/jobs/schedule', kind: 'handler', ref: ':128' },
          { label: 'handleUpdateJobSchedule → PUT /api/jobs/:id/schedule', kind: 'handler', ref: ':150' },
          { label: 'Gantt view · JobsListPanel quick-select', kind: 'ui' },
        ] },
      },
      {
        id: 'job-create', title: 'Create job', route: '/jobs/new', file: 'pages/jobs/JobCreatePage.tsx',
        root: { label: 'JobCreatePage', kind: 'page', ref: 'JobCreatePage.tsx', children: [
          { label: 'JobForm submit → handleSubmit(formData)', kind: 'handler', ref: ':9', children: [
            { label: 'createJob(formData) → POST /api/jobs (9 site fields)', kind: 'route', ref: 'jobs.routes.ts:18', children: [
              { label: '→ navigate(/jobs/:id)', kind: 'state' },
            ] },
          ] },
        ] },
      },
      {
        id: 'job-edit', title: 'Edit job', route: '/jobs/:id/edit', file: 'pages/jobs/JobEditPage.tsx',
        root: { label: 'JobEditPage', kind: 'page', ref: 'JobEditPage.tsx', children: [
          { label: 'loadJob() → getJobById(id) → GET /api/jobs/:id', kind: 'handler', ref: ':18', children: [
            { label: 'GET /api/jobs/:id', kind: 'route', ref: 'jobs.routes.ts:11' },
          ] },
          { label: 'handleSubmit → updateJob(id) → PUT /api/jobs/:id', kind: 'handler', ref: ':43', children: [
            { label: 'PUT /api/jobs/:id', kind: 'route', ref: 'jobs.routes.ts:26' },
          ] },
        ] },
      },
      {
        id: 'job-detail', title: 'Job detail (tabbed)', route: '/jobs/:id', file: 'pages/jobs/JobDetailPage.tsx',
        root: { label: 'JobDetailPage', kind: 'page', ref: 'JobDetailPage.tsx', children: [
          { label: 'loadJob() → /:id + /financials /notes /status-history /team', kind: 'handler', ref: ':83', children: [
            { label: 'GET /api/jobs/:id (+sub-resources)', kind: 'route', ref: 'jobs.routes.ts:11-17' },
          ] },
          { label: 'status badge → PUT /api/jobs/:id/status', kind: 'route', ref: 'jobs.routes.ts:28' },
          { label: 'handleCloseoutDownload()', kind: 'handler', ref: ':125', children: [
            { label: 'GET /api/jobs/:jobId/closeout', kind: 'route', ref: 'closeout.routes.ts:14' },
          ] },
          { label: 'edit modal → PUT /api/jobs/:id', kind: 'handler', ref: ':146' },
          { label: 'tab components (each owns its data flow)', kind: 'ui', children: [
            { label: 'JobNotesList → GET/POST/PUT/DELETE /api/jobs/:id/notes', kind: 'route', ref: ':270' },
            { label: 'ScheduleTab → /api/schedule + /api/tasks + /api/subtasks', kind: 'route', ref: ':277' },
            { label: 'JobTasksTab → /api/tasks (job/:jobId, subtasks, templates)', kind: 'route', ref: 'tasks.routes.ts:9' },
            { label: 'JobMaterialsList → GET/POST /api/jobs/:id/materials', kind: 'route', ref: 'jobs.routes.ts:16' },
            { label: 'EstimatesTab → /api/quotes (+ invoice-from-quote)', kind: 'route', ref: ':316' },
            { label: 'MaterialEstimatesTab → /api/estimates', kind: 'route', ref: 'estimates.ts:11' },
            { label: 'InvoicesTab → /api/invoices (list, payment, send)', kind: 'route', ref: ':351' },
            { label: 'ChangeOrdersTab → /api/change-orders/job/:jobId', kind: 'route', ref: 'changeOrders.routes.ts:15' },
            { label: 'PhotoGalleryTab → POST /api/jobs/:id/documents', kind: 'route', ref: 'jobs.routes.ts:20' },
            { label: 'DailyLogsTab → /api/jobs/:jobId/daily-logs CRUD', kind: 'route', ref: 'dailyLogs.routes.ts:8' },
            { label: 'RFIsTab → /api/jobs/:jobId/rfis CRUD', kind: 'route', ref: 'rfis.routes.ts:7' },
            { label: 'PermitsTab → /permits + /inspections', kind: 'route', ref: 'permits.routes.ts:8' },
            { label: 'ContractBuilderTab → /api/agreements (generate/issue/sign)', kind: 'route', ref: 'agreements.routes.ts:21' },
          ] },
        ] },
      },
    ],
  },

  {
    id: 'wizard', label: 'AI Quote Wizard', icon: 'auto_awesome',
    features: [
      {
        id: 'wiz-scan', title: 'Plan Scan (Step 1)', route: '/quotes/wizard', file: 'pages/estimates/QuoteWizardPage.tsx',
        root: { label: 'scanPlans()', kind: 'handler', ref: 'QuoteWizardPage.tsx:452', children: [
          { label: 'FormData ← { file, description }', kind: 'state' },
          { label: 'startJob(/api/ai/analyse-plans) ⤷ { jobId }', kind: 'api', ref: ':19', children: [
            { label: 'POST /api/ai/analyse-plans', kind: 'route', ref: 'ai.routes.ts:515', children: [
              { label: 'multer uploadScan (tmpdir, ≤50 MB, PDF)', kind: 'service', ref: ':496' },
              { label: 'createJob("scan") → jobs.set(id)', kind: 'service', ref: 'jobStore.ts:18' },
              { label: '(async) pdftoppm -r120 -f1 -l10 → PNGs', kind: 'external', ref: ':532' },
              { label: 'completeWithImages(prompt, pngs, SCAN_SYSTEM)', kind: 'external', ref: 'claude.ts:351', children: [
                { label: 'spawn claude --input-format stream-json', kind: 'external', ref: ':375' },
              ] },
              { label: 'JSON.parse → filter detectedPhases → jobDone', kind: 'service', ref: ':608' },
            ] },
          ] },
          { label: 'localStorage[wiz_scan_job] = jobId', kind: 'state', ref: ':461' },
          { label: '⟳ useEffect[scanJobId] poll → GET /api/ai/jobs/:id', kind: 'route', ref: ':358', children: [
            { label: 'done → map phases · setScanInfo · setStep(2)', kind: 'state', ref: ':366' },
            { label: 'catch (incl. 404) retries forever — no terminal on expiry/restart', kind: 'note', warn: true, ref: ':391' },
          ] },
        ] },
      },
      {
        id: 'wiz-scope', title: 'Scope & Parameters (Step 2)', route: '/quotes/wizard', file: 'pages/estimates/QuoteWizardPage.tsx',
        root: { label: 'Step 2 — Scope', kind: 'ui', ref: ':933', children: [
          { label: 'context banner: scanInfo | pdf-ready | no-PDF', kind: 'ui', ref: ':937' },
          { label: 'Scope Presets ×7 → applyPreset(phases)', kind: 'handler', ref: ':478' },
          { label: 'Phase grid (5 groups / 19 phases) → togglePhase(id)', kind: 'handler', ref: ':470' },
          { label: 'Project Parameters ×8 chip rows → setParam(key,val)', kind: 'handler', ref: ':482' },
          { label: '"Generate Quote" → runAnalysis()', kind: 'handler', ref: ':1112' },
        ] },
      },
      {
        id: 'wiz-estimate', title: 'Estimate Generation (Step 2→4)', route: '/quotes/wizard', file: 'pages/estimates/QuoteWizardPage.tsx',
        root: { label: 'runAnalysis()', kind: 'handler', ref: ':508', children: [
          { label: 'derive effectiveTitle / effectivePhases · setStep(3)', kind: 'state', ref: ':511' },
          { label: 'startJob(/api/ai/quote-wizard) ⤷ { jobId }', kind: 'api', children: [
            { label: 'POST /api/ai/quote-wizard', kind: 'route', ref: 'ai.routes.ts:634', children: [
              { label: 'pdftoppm -r150 -f1 -l10 → PNGs', kind: 'external', ref: ':660' },
              { label: 'prompt = PHASE_INSTRUCTIONS + scope + JSON schema', kind: 'service', ref: ':702' },
              { label: 'completeWithImages(prompt, pngs, ESTIMATOR_SYSTEM)', kind: 'external', ref: 'claude.ts:351', children: [
                { label: 'ESTIMATOR_SYSTEM = BC-2025 installed price book', kind: 'external', ref: 'claude.ts:251' },
                { label: 'text-only fallback → complete()', kind: 'external', ref: 'ai.routes.ts:764' },
              ] },
              { label: 'JSON.parse → jobDone({projectInfo,sections,email,judgements})', kind: 'service', ref: ':781' },
            ] },
          ] },
          { label: '⟳ useEffect[estimateJobId] poll', kind: 'route', ref: ':400', children: [
            { label: 'done → setSections · setEmail · setJudgements · setStep(4)', kind: 'state', ref: ':408' },
            { label: 'catch → infinite retry on 404', kind: 'note', warn: true, ref: ':432' },
          ] },
          { label: 'no CLI timeout — hung claude leaves job running until 4 h purge', kind: 'note', warn: true, ref: 'claude.ts:351' },
        ] },
      },
      {
        id: 'wiz-poll', title: 'Background Job Polling (shared)', file: 'server/services/jobStore.ts',
        root: { label: 'Background Job Polling', kind: 'service', children: [
          { label: 'pollJob(jobId) → fetch GET /api/ai/jobs/:id', kind: 'api', ref: 'QuoteWizardPage.tsx:31', children: [
            { label: '!res.ok → throw `Job not found` (caught → retry)', kind: 'note', warn: true, ref: ':36' },
          ] },
          { label: 'GET /api/ai/jobs/:id', kind: 'route', ref: 'ai.routes.ts:506', children: [
            { label: 'getJob(id) from Map · undefined → 404', kind: 'service', ref: 'jobStore.ts:24' },
          ] },
          { label: 'jobStore Map: createJob/jobLog/jobDone/jobFail', kind: 'service', ref: 'jobStore.ts:16', children: [
            { label: 'setInterval purge >4 h — wiped on PM2 restart', kind: 'note', warn: true, ref: ':47' },
          ] },
          { label: 'resume-on-mount: localStorage wiz_scan_job / wiz_est_job', kind: 'state', ref: 'QuoteWizardPage.tsx:343', children: [
            { label: 'est-job → setStep(3); if job gone → permanent spinner', kind: 'note', warn: true, ref: ':346' },
          ] },
        ] },
      },
      {
        id: 'wiz-review', title: 'Review & Edit (Step 4)', route: '/quotes/wizard', file: 'pages/estimates/QuoteWizardPage.tsx',
        root: { label: 'Step 4 — Review (gated on projectInfo)', kind: 'ui', ref: ':1167', children: [
          { label: 'AI summary banner + View Plans overlay', kind: 'ui', ref: ':1170' },
          { label: 'Contingency & Markup 0/5/10/15/20%/custom', kind: 'handler', ref: ':1207', children: [
            { label: 'custom % not clamped to 100', kind: 'note', warn: true, ref: ':1240' },
          ] },
          { label: 'Section tables → updateItem/addItem/removeItem/renameSection', kind: 'handler', ref: ':550' },
          { label: 'Client Email accordion (subject/body editable)', kind: 'ui', ref: ':1349' },
          { label: 'AI Judgements accordion', kind: 'ui', ref: ':1388' },
          { label: '"Create Quote" → createQuote()', kind: 'handler', ref: ':1440' },
        ] },
      },
      {
        id: 'wiz-persist', title: 'Quote Persistence (Step 4→5)', route: '/quotes/wizard', file: 'pages/estimates/QuoteWizardPage.tsx',
        root: { label: 'createQuote()', kind: 'handler', ref: ':588', children: [
          { label: 'POST /api/quotes { jobId|null, title }', kind: 'route', ref: 'quotes.routes.ts:120', children: [
            { label: '`title` ignored — quotes table has no title column', kind: 'note', warn: true, ref: 'database.sql:133' },
          ] },
          { label: 'build baseSections (+ markup LS section)', kind: 'state', ref: ':594' },
          { label: 'PUT /api/quotes/:id/sections { sections }', kind: 'route', ref: 'quotes.routes.ts:153', children: [
            { label: 'txn: DELETE+INSERT sections/line items · UPDATE subtotal=total', kind: 'service', ref: ':162' },
          ] },
          { label: 'PUT /api/quotes/:id { title, client_id }', kind: 'route', ref: ':630', children: [
            { label: 'handler reads only status → undefined → mysql2 throws → swallowed', kind: 'note', warn: true, ref: 'quotes.routes.ts:201' },
            { label: 'NET: client + title never persisted; client-only quote orphaned', kind: 'note', warn: true },
          ] },
          { label: 'setCreatedQuoteId · setStep(5)', kind: 'state', ref: ':633' },
        ] },
      },
      {
        id: 'wiz-email', title: 'Client Email Dispatch (Step 5)', route: '/quotes/wizard', file: 'pages/estimates/QuoteWizardPage.tsx',
        root: { label: 'sendEmail()', kind: 'handler', ref: ':486', children: [
          { label: 'to = clients.find(c=>c.id===clientId)?.email', kind: 'state', ref: ':489', children: [
            { label: 'no email → alert; return (requires client selected)', kind: 'note', warn: true, ref: ':491' },
          ] },
          { label: 'POST /api/ai/send-wizard-email { to, subject, body, quote_id }', kind: 'route', ref: 'ai.routes.ts:795', children: [
            { label: 'text→HTML + optional "View Quote Online" CTA', kind: 'service', ref: ':803' },
            { label: 'sendMail({ from info@ochrepoint.ca, ... })', kind: 'external', ref: 'services/mailer.ts' },
          ] },
          { label: 'setEmailSent(true) → green pill', kind: 'state', ref: ':498' },
        ] },
      },
    ],
  },

  {
    id: 'quotes', label: 'Quotes, Estimates & Agreements', icon: 'request_quote',
    features: [
      {
        id: 'quotes-list', title: 'Quotes list', route: '/quotes', file: 'pages/estimates/QuotesListPage.tsx',
        root: { label: 'QuotesListPage', kind: 'page', ref: 'QuotesListPage.tsx', children: [
          { label: 'loadQuotes() → getQuotes → GET /api/quotes', kind: 'handler', ref: ':103', children: [
            { label: 'GET /api/quotes', kind: 'route', ref: 'quotes.routes.ts:12' },
          ] },
          { label: 'handlePrintQuote(id) → GET /api/quotes/:id → print', kind: 'handler', ref: ':140' },
          { label: 'handleDeleteQuote(id) → DELETE /api/quotes/:id', kind: 'handler', ref: ':225' },
          { label: 'handleConvertToInvoice → POST /api/invoices/from-quote/:quoteId', kind: 'handler', ref: ':237' },
          { label: 'handleCreateLabourEstimate → POST /api/labour-estimates', kind: 'handler', ref: ':157' },
          { label: 'material-estimate modal → POST /api/estimates', kind: 'route', ref: 'estimates.ts:11' },
        ] },
      },
      {
        id: 'quote-builder', title: 'Quote builder', route: '/quotes/new · /quotes/:id', file: 'pages/estimates/QuoteBuilderPage.tsx',
        root: { label: 'QuoteBuilderPage', kind: 'page', ref: 'QuoteBuilderPage.tsx', children: [
          { label: 'handleSelectJob(jobId) → createQuote(jobId)', kind: 'handler', ref: ':83', children: [
            { label: 'POST /api/quotes { jobId }', kind: 'route', ref: 'quotes.routes.ts:120' },
          ] },
          { label: 'loadQuote() → GET /api/quotes/:id (sections+items+client)', kind: 'handler', ref: ':116' },
          { label: 'section/item editing (@dnd-kit) — add/delete/update', kind: 'handler', ref: ':213' },
          { label: 'saveSectionsToBackend() → PUT /api/quotes/:id/sections', kind: 'handler', ref: ':97' },
          { label: 'handleSendQuote() → POST /api/quotes/:id/send (email+audit)', kind: 'handler', ref: ':170' },
          { label: 'AI quote review → POST /api/ai/analyze-quote', kind: 'route', ref: 'ai.routes.ts:142' },
          { label: 'QuoteAuditTrail → GET /api/quotes/:id/audit', kind: 'route', ref: 'quotes.routes.ts:362' },
          { label: 'Duplicate → POST /api/quotes/:id/duplicate', kind: 'route', ref: 'quotes.routes.ts:274' },
          { label: 'ChangeOrderGenerator → POST /api/quotes/:id/change-order', kind: 'route', ref: 'quotes.routes.ts:318' },
          { label: '"Create Agreement" → /agreements/new?quoteId=…', kind: 'ui' },
        ] },
      },
      {
        id: 'estimate-detail', title: 'Material estimate detail', route: '/estimates/:id', file: 'pages/estimates/EstimateDetailPage.tsx',
        root: { label: 'EstimateDetailPage', kind: 'page', ref: 'EstimateDetailPage.tsx', children: [
          { label: 'loadEstimate() → GET /api/estimates/:id', kind: 'handler', ref: ':46' },
          { label: 'AI material suggestions → POST /api/ai/suggest-materials', kind: 'route', ref: 'ai.routes.ts:77' },
          { label: 'RFQ → POST /api/estimates/:id/rfq', kind: 'route', ref: 'estimates.ts:95' },
          { label: 'saveChanges() → PUT /api/estimates/:id', kind: 'handler', ref: ':76' },
          { label: 'handleDelete() → DELETE /api/estimates/:id', kind: 'handler', ref: ':96' },
        ] },
      },
      {
        id: 'labour-estimate', title: 'Labour estimate detail', route: '/labour-estimates/:id', file: 'pages/estimates/LabourEstimateDetailPage.tsx',
        root: { label: 'LabourEstimateDetailPage', kind: 'page', ref: 'LabourEstimateDetailPage.tsx', children: [
          { label: 'loadEstimate() → GET /api/labour-estimates/:id', kind: 'handler', ref: ':45' },
          { label: 'saveChanges() (hours×rate) → PUT /api/labour-estimates/:id', kind: 'handler', ref: ':76' },
          { label: 'handleDelete() → DELETE /api/labour-estimates/:id', kind: 'handler', ref: ':99' },
        ] },
      },
      {
        id: 'agreements-list', title: 'Agreements list', route: '/agreements', file: 'pages/agreements/AgreementsPage.tsx',
        root: { label: 'AgreementsPage', kind: 'page', ref: 'AgreementsPage.tsx', children: [
          { label: 'load() → getAgreements → GET /api/agreements', kind: 'handler', ref: ':29' },
          { label: 'create-from-quote → /agreements/new?quoteId=…', kind: 'ui' },
          { label: 'handleDelete() → DELETE /api/agreements/:id', kind: 'handler', ref: ':43' },
        ] },
      },
      {
        id: 'agreement-builder', title: 'Agreement builder', route: '/agreements/new · /agreements/:id', file: 'pages/agreements/AgreementBuilderPage.tsx',
        root: { label: 'AgreementBuilderPage (split-pane + live HTML preview)', kind: 'page', ref: 'AgreementBuilderPage.tsx', children: [
          { label: 'getAgreement(id) → GET /api/agreements/:id', kind: 'handler', ref: ':584' },
          { label: 'quote pre-fill via ?quoteId (green banner)', kind: 'state' },
          { label: 'handleAiScope() → POST /api/ai/scope-of-work', kind: 'handler', ref: ':705' },
          { label: 'handleSave() → POST|PUT /api/agreements', kind: 'handler', ref: ':725' },
          { label: 'handleSendForSigning() → POST /api/agreements/:id/send', kind: 'handler', ref: ':749' },
          { label: 'handlePrint() — export branded HTML', kind: 'handler', ref: ':772' },
        ] },
      },
      {
        id: 'agreement-sign', title: 'Agreement signing (public)', route: '/agreements/sign/:token', file: 'pages/agreements/AgreementSignPage.tsx',
        root: { label: 'AgreementSignPage (no auth)', kind: 'page', ref: 'AgreementSignPage.tsx', children: [
          { label: 'on mount → GET /api/agreements/sign/:token', kind: 'route', ref: 'agreements.routes.ts:17' },
          { label: 'handleSign() → POST /api/agreements/sign/:token', kind: 'handler', ref: ':33' },
        ] },
      },
    ],
  },

  {
    id: 'team', label: 'Clients, Employees & Crews', icon: 'groups',
    features: [
      {
        id: 'clients', title: 'Clients', route: '/clients · /clients/:id', file: 'pages/clients/ClientsPage.tsx',
        root: { label: 'ClientsPage (list + right-panel detail)', kind: 'page', ref: 'ClientsPage.tsx', children: [
          { label: 'getClients() → GET /api/clients', kind: 'handler', ref: ':514' },
          { label: 'loadClientDetails(id) → GET /api/clients/:id', kind: 'handler', ref: ':128' },
          { label: 'create/edit/delete → POST/PUT/DELETE /api/clients', kind: 'handler', ref: ':591' },
          { label: 'panel loaders: estimates / labour / quotes / invoices', kind: 'handler', ref: ':149' },
          { label: 'Invoices subpanel: create/save/send/pay/delete', kind: 'handler', ref: ':214', children: [
            { label: 'POST|PUT /api/invoices · /:id/send · /:id/payments', kind: 'route', ref: 'invoices.routes.ts:49' },
          ] },
          { label: 'handleCreateQuoteFromEstimates → POST /api/quotes/from-estimate', kind: 'handler', ref: ':417' },
          { label: 'portal invite → POST /api/invites', kind: 'route', ref: 'invites.routes.ts:10' },
        ] },
      },
      {
        id: 'employees', title: 'Employees & HR', route: '/employees · /employees/:id', file: 'pages/employees/EmployeesPage.tsx',
        root: { label: 'EmployeesPage (list + tabbed detail)', kind: 'page', ref: 'EmployeesPage.tsx', children: [
          { label: 'loadEmployees() → GET /api/employees', kind: 'handler', ref: ':199' },
          { label: 'handleSubmit → create/updateEmployee', kind: 'handler', ref: ':294' },
          { label: 'loadTabData(tab,id) — lazy per-tab fetch', kind: 'handler', ref: ':89', children: [
            { label: 'Schedule → GET /api/employees/:id/schedule', kind: 'route', ref: 'employees.routes.ts:26' },
            { label: 'Paystubs → GET /api/employees/:id/paystubs', kind: 'route', ref: 'employees.routes.ts:51' },
            { label: 'Documents → GET /api/employees/:id/documents', kind: 'route', ref: 'employees.routes.ts:68' },
            { label: 'Time Off → GET …/timeoff', kind: 'route', ref: 'employees.routes.ts:94' },
            { label: 'Skills → GET …/skills', kind: 'route', ref: 'employees.routes.ts:139' },
          ] },
          { label: 'Time Off: add/status/delete', kind: 'handler', ref: ':108', children: [
            { label: 'POST/PATCH/DELETE …/timeoff[/:toid]', kind: 'route', ref: 'employees.routes.ts:102' },
          ] },
          { label: 'Skills: add/edit/delete', kind: 'handler', ref: ':144', children: [
            { label: 'POST/PATCH/DELETE …/skills[/:skillId]', kind: 'route', ref: 'employees.routes.ts:147' },
          ] },
          { label: 'portal invite → POST /api/invites', kind: 'route', ref: 'invites.routes.ts:10' },
        ] },
      },
      {
        id: 'crews', title: 'Crews', route: '/crews · /crews/:id', file: 'pages/employees/CrewsPage.tsx',
        root: { label: 'CrewsPage', kind: 'page', ref: 'CrewsPage.tsx', children: [
          { label: 'loadCrews() → GET /api/crews', kind: 'handler', ref: ':44' },
          { label: 'loadCrewDetail(id) → GET /api/crews/:id', kind: 'handler', ref: ':63' },
          { label: 'handleCreateCrew → POST /api/crews', kind: 'handler', ref: ':81' },
          { label: 'handleSaveEdit/AddMember/RemoveMember → PUT /api/crews/:id', kind: 'handler', ref: ':98' },
          { label: 'handleDeleteCrew → DELETE /api/crews/:id', kind: 'handler', ref: ':153' },
        ] },
      },
      {
        id: 'timesheets', title: 'Timesheet approvals', route: '/timesheets', file: 'pages/employees/TimesheetApprovalsPage.tsx',
        root: { label: 'TimesheetApprovalsPage', kind: 'page', ref: 'TimesheetApprovalsPage.tsx', children: [
          { label: 'load() → GET /api/portal/time-entries (approver queue)', kind: 'handler', ref: ':23' },
          { label: 'handle(id, action)', kind: 'handler', ref: ':36', children: [
            { label: 'approve → POST /api/portal/time-entries/:id/approve', kind: 'route', ref: 'portal.routes.ts:259' },
            { label: 'reject → POST /api/portal/time-entries/:id/reject', kind: 'route', ref: 'portal.routes.ts:267' },
          ] },
        ] },
      },
    ],
  },

  {
    id: 'finance', label: 'Invoices, Payroll, Catalog, Schedule', icon: 'payments',
    features: [
      {
        id: 'invoices', title: 'Invoices', route: '/invoices', file: 'pages/invoices/InvoicesPage.tsx',
        root: { label: 'InvoicesPage (thin; modals carry writes)', kind: 'page', ref: 'InvoicesPage.tsx', children: [
          { label: 'load() → getInvoices → GET /api/invoices', kind: 'handler', ref: ':38' },
          { label: 'InvoiceModal → POST|PUT /api/invoices[/:id]', kind: 'route', ref: 'invoices.routes.ts:49' },
          { label: 'InvoiceDetailModal → GET /api/invoices/:id', kind: 'route', ref: 'invoices.routes.ts:46' },
          { label: 'RecordPaymentModal → POST /api/invoices/:id/payments', kind: 'route', ref: 'invoices.routes.ts:61' },
          { label: 'SendInvoiceModal → POST /api/invoices/:id/send', kind: 'route', ref: 'invoices.routes.ts:58' },
          { label: 'from-quote → POST /api/invoices/from-quote/:quoteId', kind: 'route', ref: 'invoices.routes.ts:35' },
          { label: 'attachments: upload / from-document / reorder / delete', kind: 'route', ref: 'invoices.routes.ts:38-43' },
        ] },
      },
      {
        id: 'payroll', title: 'Payroll', route: '/payroll', file: 'pages/payroll/PayrollPage.tsx',
        root: { label: 'PayrollPage', kind: 'page', ref: 'PayrollPage.tsx', children: [
          { label: 'loadRuns() → GET /api/payroll', kind: 'handler', ref: ':880' },
          { label: 'load(runId) → GET /api/payroll/:id', kind: 'handler', ref: ':631' },
          { label: 'handleSubmit (create run) → POST /api/payroll', kind: 'handler', ref: ':471' },
          { label: 'handleSave (entry) → PATCH /api/payroll/entries/:entryId', kind: 'handler', ref: ':101' },
          { label: 'handleStatusChange → PATCH /api/payroll/:id/status', kind: 'handler', ref: ':648' },
          { label: 'deductions → POST/DELETE /api/payroll/.../deductions', kind: 'route', ref: 'payroll.routes.ts:15' },
        ] },
      },
      {
        id: 'catalog', title: 'Catalog', route: '/catalog', file: 'pages/catalog/CatalogPage.tsx',
        root: { label: 'CatalogPage', kind: 'page', ref: 'CatalogPage.tsx', children: [
          { label: 'fetchClientsAndJobs() → GET /api/clients + /api/jobs', kind: 'handler', ref: ':590' },
          { label: 'fetchProducts() → GET /api/catalog/products (dim search)', kind: 'handler', ref: ':631', children: [
            { label: 'GET /api/catalog/products · /categories', kind: 'route', ref: 'catalogRoutes.ts:11' },
          ] },
          { label: 'cart: add/qty/delete/dragEnd', kind: 'handler', ref: ':886' },
          { label: 'handleSaveEdit → PUT /api/catalog/products/:id', kind: 'handler', ref: ':710' },
          { label: 'price-check → POST /api/catalog/products/price-check', kind: 'route', ref: 'catalogRoutes.ts:32' },
          { label: 'ImportPanel → POST /api/catalog/scrape · /parse-html', kind: 'external', ref: 'catalogRoutes.ts:26' },
          { label: 'push cart → estimate → POST /api/estimates', kind: 'handler', ref: ':1477' },
        ] },
      },
      {
        id: 'schedule', title: 'Schedule / Calendar', route: '/calendar', file: 'pages/schedule/CalendarPage.tsx',
        root: { label: 'CalendarPage (thin) → JobTimeline', kind: 'page', ref: 'CalendarPage.tsx', children: [
          { label: 'getScheduleEvents(range) → GET /api/schedule/events', kind: 'handler', ref: 'JobTimeline.tsx:380' },
          { label: 'TaskBank → GET /api/schedule/unassigned', kind: 'route', ref: 'schedule.routes.ts:99' },
          { label: 'create entry → POST /api/schedule/events (+crew SMS/email)', kind: 'route', ref: 'schedule.routes.ts:130', children: [
            { label: 'Twilio SMS / email to crew', kind: 'external' },
          ] },
          { label: 'drag-drop/resize → PUT /api/schedule/events/:id', kind: 'route', ref: 'schedule.routes.ts:189' },
          { label: 'assign → POST /api/schedule/assign', kind: 'route', ref: 'schedule.routes.ts:315' },
          { label: 'checklist → PATCH /api/schedule/checklist/:id', kind: 'route', ref: 'schedule.routes.ts:283' },
          { label: 'client reminder → POST /api/schedule/events/:id/notify', kind: 'route', ref: 'schedule.routes.ts:418' },
          { label: 'conflicts → GET /api/schedule/conflicts', kind: 'route', ref: 'schedule.routes.ts:366' },
          { label: 'AI suggestions → POST /api/ai/schedule-suggest', kind: 'route', ref: 'ai.routes.ts:203' },
        ] },
      },
      {
        id: 'vendors', title: 'Vendors', route: '/vendors', file: 'pages/vendors/VendorsPage.tsx',
        root: { label: 'VendorsPage', kind: 'page', ref: 'VendorsPage.tsx', children: [
          { label: 'load() → GET /api/vendors', kind: 'handler', ref: ':216' },
          { label: 'handleAddSubmit → POST /api/vendors', kind: 'handler', ref: ':234' },
          { label: 'handleEditSave(id) → PUT /api/vendors/:id', kind: 'handler', ref: ':259' },
          { label: 'handleDelete(id) → DELETE /api/vendors/:id', kind: 'handler', ref: ':272' },
        ] },
      },
    ],
  },

  {
    id: 'admin', label: 'Reports, Docs, Settings & Dev', icon: 'tune',
    features: [
      {
        id: 'reports', title: 'Reports', route: '/reports', file: 'pages/reports/ReportsPage.tsx',
        root: { label: 'ReportsPage', kind: 'page', ref: 'ReportsPage.tsx', children: [
          { label: 'load() (per active report)', kind: 'handler', ref: ':51', children: [
            { label: 'GET /api/reports/revenue', kind: 'route', ref: 'reports.routes.ts:9' },
            { label: 'GET /api/reports/ar-aging', kind: 'route', ref: 'reports.routes.ts:10' },
            { label: 'GET /api/reports/profitability', kind: 'route', ref: 'reports.routes.ts:11' },
            { label: 'GET /api/reports/crew-utilization', kind: 'route', ref: 'reports.routes.ts:12' },
          ] },
          { label: 'handleDataExport() → GET /api/export/all', kind: 'handler', ref: ':462' },
          { label: 'inventory lists /win-loss — no such route exists', kind: 'note', warn: true },
        ] },
      },
      {
        id: 'projections', title: 'Projections', route: '/projections', file: 'pages/reports/ProjectionsPage.tsx',
        root: { label: 'ProjectionsPage', kind: 'page', ref: 'ProjectionsPage.tsx', children: [
          { label: 'load() → GET /api/projections', kind: 'handler', ref: ':76', children: [
            { label: 'monthly actuals + seasonal forecast + pipeline', kind: 'service', ref: 'projections.routes.ts:28' },
          ] },
          { label: 'seasonal-weight editor (client recompute)', kind: 'state' },
        ] },
      },
      {
        id: 'documents', title: 'Documents', route: '/documents', file: 'pages/documents/DocumentsPage.tsx',
        root: { label: 'DocumentsPage', kind: 'page', ref: 'DocumentsPage.tsx', children: [
          { label: 'list/filter → GET /api/documents/job/:jobId · /:id · /:id/render', kind: 'route', ref: 'documents.routes.ts:10' },
          { label: 'create/edit project docs → POST/PUT /api/documents/project', kind: 'route' },
          { label: 'status → PATCH /api/documents/:id/status', kind: 'route', ref: 'documents.routes.ts:65' },
          { label: 'preview/print single doc → branded HTML', kind: 'handler', ref: ':125' },
          { label: 'closeout package → GET /api/jobs/:jobId/closeout', kind: 'handler', ref: ':2095' },
        ] },
      },
      {
        id: 'settings', title: 'Settings', route: '/settings', file: 'pages/settings/SettingsPage.tsx',
        root: { label: 'SettingsPage (tabbed)', kind: 'page', ref: 'SettingsPage.tsx', children: [
          { label: 'loadSettings() → GET /api/settings', kind: 'handler', ref: ':202' },
          { label: 'handleSave() → PUT /api/settings (per-category)', kind: 'handler', ref: ':214' },
          { label: 'IntegrationsTab', kind: 'ui', ref: 'IntegrationsTab.tsx', children: [
            { label: 'status → GET /api/payments/status · /integrations/sms/status', kind: 'route', ref: ':48' },
            { label: 'Google connect/sync → /integrations/google/*', kind: 'route', ref: 'integrations.routes.ts:38' },
            { label: 'Drive files/upload/backup → /integrations/drive/*', kind: 'external', ref: 'integrations.routes.ts:102' },
          ] },
          { label: 'InvitesTab → GET/POST/DELETE /api/invites', kind: 'ui', ref: 'InvitesTab.tsx', children: [
            { label: 'load/create/revoke', kind: 'route', ref: 'invites.routes.ts:9' },
          ] },
        ] },
      },
      {
        id: 'dev', title: 'Dev dashboard', route: '/dev', file: 'pages/dev/DevDashboardPage.tsx',
        root: { label: 'DevDashboardPage', kind: 'page', ref: 'DevDashboardPage.tsx', children: [
          { label: 'loadAll() → GET /api/dev/summary · /features · /roadmap', kind: 'handler', ref: ':681' },
          { label: 'loadTasks() → GET /api/dev/tasks', kind: 'handler', ref: ':696' },
          { label: 'feature/task/roadmap CRUD → POST/PUT/DELETE /api/dev/*', kind: 'route', ref: 'dev.routes.ts:63' },
          { label: 'handleBackup() → POST /api/dev/backup-code', kind: 'handler', ref: ':703' },
          { label: 'handleSqlBackup() → POST /api/database/backup', kind: 'handler', ref: ':716' },
          { label: 'Portal Preview overlay → POST /api/dev/ensure-preview-accounts', kind: 'route', ref: 'dev.routes.ts:205' },
        ] },
      },
      {
        id: 'my-tasks', title: 'My Tasks', route: '/my-tasks', file: 'pages/admin/AdminTasksPage.tsx',
        root: { label: 'AdminTasksPage (localStorage only)', kind: 'page', ref: 'AdminTasksPage.tsx', children: [
          { label: 'daily routine checklist + custom to-do', kind: 'ui' },
          { label: 'progress bar (derived) · reset-routine', kind: 'state' },
          { label: 'persistence → localStorage (no API)', kind: 'state' },
        ] },
      },
      {
        id: 'co-approve', title: 'Change-order approval (public)', route: '/change-orders/approve/:token', file: 'pages/changeOrders/ChangeOrderApprovePage.tsx',
        root: { label: 'ChangeOrderApprovePage (no auth)', kind: 'page', ref: 'ChangeOrderApprovePage.tsx', children: [
          { label: 'handleApprove(e) → approveChangeOrderByToken(token, name)', kind: 'handler', ref: ':13', children: [
            { label: 'POST /api/change-orders/approve/:token', kind: 'route', ref: 'changeOrders.routes.ts:11', children: [
              { label: 'approved → notify admins · already-approved branch', kind: 'service' },
            ] },
          ] },
        ] },
      },
      {
        id: 'static', title: 'Static / content pages', file: 'pages/*',
        root: { label: 'Static content pages', kind: 'ui', children: [
          { label: 'RoadmapPage → GET /api/dev/roadmap', kind: 'route', ref: 'dev.routes.ts:134' },
          { label: 'HelpCenterPage — static (3 editions, 33+ articles)', kind: 'ui', ref: 'HelpCenterPage.tsx' },
          { label: 'BusinessGuidePage — static (5 chapters × 3 articles)', kind: 'ui', ref: 'BusinessGuidePage.tsx' },
          { label: 'PrivacyPage — static policy, no auth', kind: 'ui', ref: 'PrivacyPage.tsx' },
        ] },
      },
    ],
  },

  {
    id: 'portal', label: 'Portal (Employee & Client)', icon: 'badge',
    features: [
      {
        id: 'portal-auth', title: 'Portal auth', route: '/portal/login', file: 'portal/pages/auth/*',
        root: { label: 'Portal auth', kind: 'ui', children: [
          { label: 'PortalLoginPage → portalLogin → POST /api/portal/auth/login', kind: 'handler', ref: 'PortalLoginPage.tsx:28', children: [
            { label: 'POST /api/portal/auth/login', kind: 'route', ref: 'portalAuth.routes.ts:87' },
          ] },
          { label: 'PortalRegisterPage → POST /api/portal/auth/register', kind: 'route', ref: 'portalAuth.routes.ts:26' },
          { label: 'PortalCallbackPage → GET /api/portal/auth/google/url', kind: 'route', ref: 'portalAuth.routes.ts:132' },
        ] },
      },
      {
        id: 'portal-employee', title: 'Employee portal', route: '/portal/employee', file: 'portal/pages/employee/*',
        root: { label: 'Employee portal', kind: 'ui', children: [
          { label: 'EmployeeHomePage', kind: 'page', ref: 'EmployeeHomePage.tsx', children: [
            { label: 'getTimeEntries() → GET /api/portal/me/time-entries', kind: 'route', ref: 'portal.routes.ts:188' },
            { label: 'quick log-hours → POST /me/time-entries', kind: 'route', ref: 'portal.routes.ts:199' },
            { label: 'FeatureInventory "GPS clock-in" widget — not present in code', kind: 'note', warn: true },
          ] },
          { label: 'TimesheetPage', kind: 'page', ref: 'TimesheetPage.tsx', children: [
            { label: 'create/update/submit time entries', kind: 'route', ref: 'portal.routes.ts:199' },
            { label: 'documented "GPS Time Clock" + clock endpoints + time_clock_sessions — none exist', kind: 'note', warn: true },
          ] },
          { label: 'EmployeeSchedulePage → GET /me/schedule', kind: 'route', ref: 'portal.routes.ts:36' },
          { label: 'EmployeeDocumentsPage → GET /me/documents', kind: 'route', ref: 'portal.routes.ts:97' },
          { label: 'PaystubsPage → GET /me/paystubs', kind: 'route', ref: 'portal.routes.ts:311' },
          { label: 'NotificationSettingsPage → GET|PUT /me/notification-prefs', kind: 'route', ref: 'portal.routes.ts:139' },
        ] },
      },
      {
        id: 'portal-client', title: 'Client portal', route: '/portal/client', file: 'portal/pages/client/*',
        root: { label: 'Client portal', kind: 'ui', children: [
          { label: 'ClientHomePage → getPortalJobs/Schedule', kind: 'route', ref: 'portal.routes.ts:71' },
          { label: 'ClientSchedulePage → GET /me/schedule', kind: 'route', ref: 'portal.routes.ts:36' },
          { label: 'ClientDocumentsPage → GET /me/documents (Bearer blob)', kind: 'route', ref: 'portal.routes.ts:97' },
          { label: 'ClientInvoicesPage', kind: 'page', children: [
            { label: 'getPortalInvoices() → GET /me/invoices', kind: 'route', ref: 'portal.routes.ts:325' },
            { label: '"Pay Online" → GET /me/invoices/:id/payment-link', kind: 'route', ref: 'portal.routes.ts:345', children: [
              { label: 'Stripe → POST /api/payments/create-link/:invoiceId', kind: 'external', ref: 'payments.routes.ts:10' },
            ] },
          ] },
          { label: 'ClientOnboardingPage → PUT /me/client-profile', kind: 'route', ref: 'portal.routes.ts:382' },
          { label: 'ProfilePage → client-profile + change-password', kind: 'route', ref: 'portal.routes.ts:369' },
        ] },
      },
      {
        id: 'portal-backend', title: 'Portal backend extras + drift', file: 'server/routes/portal.routes.ts',
        root: { label: '/api/portal extras', kind: 'route', children: [
          { label: 'approver: GET /time-entries · POST /:id/approve|reject', kind: 'route', ref: 'portal.routes.ts:248' },
          { label: 'POST /sms/inbound — Twilio hour-logging (sig-validated)', kind: 'external', ref: 'portal.routes.ts:276' },
          { label: 'DRIFT: clock-status/clock-in/clock-out routes, getClockStatus/clockIn/clockOut, time_clock_sessions table — all absent (per agent-log Session 25 but not in code)', kind: 'note', warn: true },
        ] },
      },
    ],
  },
];

export function countNodes(node: TreeNode): number {
  return 1 + (node.children?.reduce((a, c) => a + countNodes(c), 0) ?? 0);
}

export function countWarnings(node: TreeNode): number {
  return (node.warn ? 1 : 0) + (node.children?.reduce((a, c) => a + countWarnings(c), 0) ?? 0);
}
